// ============================================
// Path: /frontend/src/pages/admin/AdminDashboard.jsx
// Purpose: Admin dashboard with stats and analytics charts
// ============================================

// ========================================
// IMPORTS
// ========================================
import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../../api/admin.api';
import { Loader2, List, Users, Clock, DollarSign, BarChart, LineChart } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '../../components/ui/chart';
import { Bar, BarChart as RechartsBarChart, Line, LineChart as RechartsLineChart, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip } from 'recharts';
import { subDays, format, startOfDay } from 'date-fns';
import ChamaInterestsPanel from '../../components/admin/ChamaInterestsPanel';

// ========================================
// HELPER FUNCTIONS
// ========================================

const processDataForChart = (data, days) => {
  if (!data || !Array.isArray(data)) return [];
  
  // Backend already returns {date, count} format from SQL query
  // Just format the dates nicely
  return data.map(item => ({
    date: format(new Date(item.date), 'MMM d'),
    count: item.count || 0
  }));
};

const chartConfig = {
  desktop: {
    label: "Desktop",
    color: "hsl(var(--chart-1))",
  },
};

// ========================================
// MAIN COMPONENT
// ========================================
export default function AdminDashboard() {
  console.log('🎨 [AdminDashboard] Component mounted');

  // ========================================
  // COMPONENT STATE
  // ========================================
  const [stats, setStats] = useState(null);
  const [listingsData, setListingsData] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ========================================
  // DATA FETCHING
  // ========================================
  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    console.log('🔵 [AdminDashboard] Fetching dashboard data...');
    setLoading(true);

    try {
      const [statsResponse, listingsResponse, usersResponse] = await Promise.all([
        adminAPI.getStats(),
        adminAPI.getListingAnalytics(),
        adminAPI.getUserAnalytics(),
      ]);

      console.log('✅ [AdminDashboard] Stats received:', statsResponse);
      console.log('✅ [AdminDashboard] Listings data received:', listingsResponse);
      console.log('✅ [AdminDashboard] Users data received:', usersResponse);

      setStats(statsResponse.stats);
      setListingsData(listingsResponse.analytics?.growth || []);
      setUsersData(usersResponse.analytics?.growth || []);
      setError(null);

    } catch (err) {
      console.error('❌ [AdminDashboard] Fetch error:', err);
      setError(err.response?.data?.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // COMPUTED DATA
  // ========================================
  const listingChartData = useMemo(() => {
    console.log('🔄 [AdminDashboard] Processing listing chart data');
    return processDataForChart(listingsData, 7);
  }, [listingsData]);

  const userChartData = useMemo(() => {
    console.log('🔄 [AdminDashboard] Processing user chart data');
    return processDataForChart(usersData, 7);
  }, [usersData]);

  // ========================================
  // LOADING STATE
  // ========================================
  if (loading) {
    console.log('⏳ [AdminDashboard] Loading...');
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  // ========================================
  // ERROR STATE
  // ========================================
  if (error) {
    console.log('🔴 [AdminDashboard] Error state:', error);
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-destructive mb-4">Error loading dashboard: {error}</p>
          <button onClick={fetchDashboardData} className="text-primary underline">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ========================================
  // RENDER
  // ========================================
  console.log('🎨 [AdminDashboard] Rendering with stats:', stats);

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">Admin Dashboard</h1>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Link to="/admin/listings">
          <Card className="hover:bg-muted/50 transition-colors">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Listings</CardTitle>
              <List className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{(stats?.totalListings || 0).toLocaleString()}</div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/super-admin">
          <Card className="hover:bg-muted/50 transition-colors">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{(stats?.totalUsers || 0).toLocaleString()}</div>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/listings">
          <Card className="hover:bg-muted/50 transition-colors">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Pending Approvals</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.pendingListings || 0}</div>
              <p className="text-xs text-muted-foreground">Listings awaiting review</p>
            </CardContent>
          </Card>
        </Link>
        <Link to="/admin/payments">
          <Card className="hover:bg-muted/50 transition-colors">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">KES {(stats?.totalRevenue || 0).toLocaleString()}</div>
              <p className="text-xs text-muted-foreground">From completed payments</p>
            </CardContent>
          </Card>
        </Link>
      </div>
      <div className="mt-8 grid gap-8 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart className="w-5 h-5" />
              Listing Trends
            </CardTitle>
            <CardDescription>New listings over the last 7 days.</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-48 w-full">
              <RechartsBarChart data={listingChartData} accessibilityLayer>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                <RechartsTooltip cursor={false} content={<ChartTooltipContent />} />
                <Bar dataKey="count" fill="var(--color-desktop)" radius={4} />
              </RechartsBarChart>
            </ChartContainer>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LineChart className="w-5 h-5" />
              User Signups
            </CardTitle>
            <CardDescription>New users over the last 7 days.</CardDescription>
          </CardHeader>
          <CardContent>
            <ChartContainer config={chartConfig} className="h-48 w-full">
              <RechartsLineChart data={userChartData} accessibilityLayer>
                <CartesianGrid vertical={false} />
                <XAxis dataKey="date" tickLine={false} axisLine={false} tickMargin={8} />
                <YAxis allowDecimals={false} tickLine={false} axisLine={false} />
                <RechartsTooltip cursor={false} content={<ChartTooltipContent />} />
                <Line dataKey="count" type="monotone" stroke="var(--color-desktop)" strokeWidth={2} dot={true} />
              </RechartsLineChart>
            </ChartContainer>
          </CardContent>
        </Card>
      </div>
     
      <div className="mt-8">
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 md:grid-cols-2">
              {/* Recent Users */}
              <div>
                <h3 className="font-semibold text-sm mb-3">Recent User Signups</h3>
                {stats?.recent?.users && stats.recent.users.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent.users.map((user) => (
                      <div key={user.id} className="flex items-center gap-3 text-sm border-b pb-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <div className="flex-1">
                          <p className="font-medium">{user.fullName}</p>
                          <p className="text-xs text-muted-foreground">{user.email}</p>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(user.createdAt), 'MMM d')}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No recent users.</p>
                )}
              </div>

              {/* Recent Listings */}
              <div>
                <h3 className="font-semibold text-sm mb-3">Recent Listings</h3>
                {stats?.recent?.listings && stats.recent.listings.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent.listings.map((listing) => (
                      <div key={listing.id} className="flex justify-between items-start text-sm border-b pb-2">
                        <div className="flex-1">
                          <p className="font-medium">{listing.title}</p>
                          <p className="text-xs text-muted-foreground">
                            by {listing.user?.fullName || 'Unknown'}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="text-xs bg-muted px-2 py-1 rounded block mb-1">
                            {listing.status}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(listing.createdAt), 'MMM d')}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">No recent listings.</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* CHAMA INTERESTS SECTION */}
      <div className="mt-8">
        <ChamaInterestsPanel />
      </div>
    </div>
  );
}